import { Validators } from '@angular/forms';

import { TenancyColumns } from '@miq/catalog';
import { formatNumber, isMasked } from '@miq/utils';
import {
  formatDate,
  generateLastUpdatedByMessage,
  isTenantEntityActive,
} from '../../../../../../shared/utils/functions';
import { TenantSpaceClass } from '../../../../../../models/space/tenant-space.class';
import { AreaUrl } from '../../details-overlay/details-overlay.constants';
import { DetailsOverlayData } from '../../details-overlay/details-overlay.model';
import { SuiteType } from '../../item.model';
import { getColumnLabel, yearsAndMonthsTerm } from '../details-modal.constants';
import {
  notEmptyRentUOM,
  notEmptySpaceUOM,
  spaceSizeLessThanFloorSize,
  leaseDateValidator,
} from '../details-modal.validators';

export const TENANT_CONFIG: DetailsOverlayData<TenantSpaceClass> = {
  id: space => space.tenantId.toString(),
  title: space => space.companyName,
  navigateToEntity: space => {
    const isTenantActive = isTenantEntityActive();
    const entityName = !isTenantActive ? 'company' : 'tenancy';
    const entityId = !isTenantActive ? space.tenant.company_id : space.tenantId;
    return entityId ? `records/${entityName}/${entityId}/edit` : null;
  },
  validators: [
    {
      groupName: 'formGroupSpace',
      validator: [notEmptySpaceUOM, spaceSizeLessThanFloorSize],
    },
    { groupName: 'formGroupRentRate', validator: [notEmptyRentUOM] },
  ],
  subsets: [
    {
      label: 'overlayDetails.termTime',
      inputType: 'progressBar',
      labelFn: space => {
        const { startDate, endDate } = space.getSpaceDates();
        const isDateMasked = isMasked(startDate) && isMasked(endDate);
        const isDateMissing = !startDate || !endDate;
        const mask = '*****';
        return isDateMissing ? null : isDateMasked ? mask : yearsAndMonthsTerm(startDate, endDate);
      },
      displayFn: space => {
        const { startDate, endDate } = space.getSpaceDates();
        const { termPercentage } = yearsAndMonthsTerm(startDate, endDate);
        return termPercentage ?? 0;
      },
    },
    {
      labelFn: columns => getColumnLabel(columns, TenancyColumns.FloorSuite_Suite),
      dataField: 'suiteNumber',
      inputType: 'textbox',
      displayFn: space => space.suiteNumber,
      validators: [Validators.required],
      class: 'suite',
    },
    {
      dataField: 'spaceSize',
      formGroup: 'formGroupSpace',
      inputType: 'numeric',
      unit: {
        dataField: 'spaceSizeUOM',
        formGroup: 'formGroupSpace',
        inputType: 'dropdown',
        lookupCategory: AreaUrl,
        displayFn: space => space.getAreaSpaceUOM(),
      },
      displayFn: (space, column) => {
        const spaceSize = space.getAreaSpace();
        if (spaceSize === null) {
          return null;
        }
        return formatNumber(spaceSize, 'en-US', column?.options?.format ?? '.0-2');
      },
      labelFn: columns => getColumnLabel(columns, TenancyColumns.FloorSuite_SpaceSize),
    },
    {
      dataField: 'leaseStartDate',
      inputType: 'date',
      validators: [leaseDateValidator('start')],
      displayFn: space => {
        const { startDate } = space.getSpaceDates();
        return isMasked(startDate) ? startDate : formatDate(startDate);
      },
      labelFn: columns => getColumnLabel(columns, TenancyColumns.FloorSuite_StartDate),
    },
    {
      dataField: 'leaseEndDate',
      inputType: 'date',
      validators: [leaseDateValidator('end')],
      displayFn: space => {
        const { endDate } = space.getSpaceDates();
        return isMasked(endDate) ? endDate : formatDate(endDate);
      },
      labelFn: columns => getColumnLabel(columns, TenancyColumns.FloorSuite_EndDate),
    },
    {
      displayFn: space => space.generateTenantRepAgency(),
      labelFn: columns => getColumnLabel(columns, TenancyColumns.TenancyDetails_CompanyName),
    },
    {
      dataField: 'industryNaicsId',
      inputType: 'naics',
      lookupCategory: '/lookup/naics',
      displayFn: space => space.tenant.ref_industry_classification_desc,
      labelFn: columns => getColumnLabel(columns, TenancyColumns.TenancyDetails_SpaceUsageNAICS),
    },
    {
      displayFn: space => space.tenant.ref_property_usage_type_desc,
      inputType: 'dropdown',
      lookupCategory: '/lookup/ref/country?lookupcategory=miq_property_usage',
      labelFn: columns => getColumnLabel(columns, TenancyColumns.FloorSuite_PropertyType),
    },
    {
      dataField: 'rentRate',
      inputType: 'currency',
      formGroup: 'formGroupRentRate',
      unit: {
        dataField: 'rentRateUOM',
        inputType: 'dropdown',
        formGroup: 'formGroupRentRate',
        lookupCategory: '/lookup/ref/country?lookupcategory=currency',
        displayFn: space => space.generateRentRateUOM(),
      },
      displayFn: (space, column) => {
        const rentRate = space.generateRentRateValue();
        if (rentRate === null) {
          return null;
        }
        return `$ ${formatNumber(rentRate, 'en-US', column?.options?.format ?? '0.2-2')}`;
      },
      labelFn: columns => getColumnLabel(columns, TenancyColumns.FloorSuite_BaseRent),
    },
    {
      labelFn: columns => getColumnLabel(columns, TenancyColumns.FloorSuite_CompID, 'overlayDetails.leaseComps'),
      displayFn: space => (space?.lease?.id != null ? space?.leaseId.toString() : null),
      navigateToEntity: space => (space.leaseId ? `records/${SuiteType.Lease}/${space.leaseId}/edit` : null),
    },
    {
      displayFn: space => space.getBrandName(),
      navigateToEntity: space => (space.getBrandId() ? `records/${SuiteType.Brand}/${space.getBrandId()}/edit` : null),
      labelFn: columns => getColumnLabel(columns, TenancyColumns.Brand_BrandName),
    },
    {
      labelFn: columns => getColumnLabel(columns, TenancyColumns.Brand_BrandCategory),
      displayFn: space =>
        space.tenant.brand_id != null ? space.tenant.business_category_desc : space?.lease?.business_category_desc,
    },
    {
      labelFn: columns => getColumnLabel(columns, TenancyColumns.TenancyDetails_TenancyNotes),
      dataField: 'notes',
      inputType: 'textarea',
      class: 'notes',
      displayFn: space => space.tenant.tenancy_notes,
    },
  ],
  footer: {
    label: 'overlayDetails.updateBy',
    displayFn: space => {
      const { lastUpdatedDate, lastUpdatedBy } = space.getRecordTimestamp();
      return generateLastUpdatedByMessage(lastUpdatedDate, lastUpdatedBy);
    },
  },
};
